<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <div id="desktop-order-history" class="d-none d-md-block">
      <div class="order-history-desktop-stack">
        <BlockContainer variant="full" :title="$t('orderHistory.subTitle.filter')" role="region">
            <date-search-table :useFutureFilters="true" :callback="retrieveOrders" ref="dateSearchTable" />
          <template #actions>
            <Button variant="primary" id="searchBtn" type="submit" @click.stop="onSearch">{{ $t('orderHistory.button.search') }}</Button>
          </template>
        </BlockContainer>

        <BlockContainer variant="full" :title="$t('orderHistory.subTitle.overview')" ref="orderOverview" role="region">
          <div>
            <h3 class="normalText sr-only">{{ $t('orderHistory.label.table') }}</h3>
            <Table
              small
              :items="recentOrders"
              :fields="fieldsWithActions"
              :responsive="true"
              :striped="true"
              :hover="true"
              @sort-changed="onSortChanged"
              @row-clicked="showOrderDetails"
              :tbody-tr-attr="rowIdentifier"
              :actions="tableActions"
              @details="openOrderDetails"
              @confirm-order="onConfirmOrder"
              @cancel-order="onCancelOrder"
              :show-empty="true"
            >
              <template #custom-foot>
                <tr>
                  <td class="text-left" colspan="5">&nbsp;</td>
                </tr>
              </template>
            </Table>
          </div>
        </BlockContainer>
      </div>
    </div>

    <div id="mobile-order-history" class="d-md-none">
      <div class="order-history-mobile-stack">
        <div class="mobile-filter-container">
          <date-search-table :useFutureFilters="true" :callback="retrieveOrders" ref="dateSearchTable" />
          <Button variant="primary" id="searchBtnMobile" type="submit" class="mobile-search-btn" @click.stop="onSearch">
            {{ $t('orderHistory.button.search') }}
          </Button>
        </div>
        <h3 class="normalText sr-only">{{ $t('orderHistory.label.table') }}</h3>
        <CardList
          :items="recentOrders"
          :list-aria-label="$t('orderHistory.label.table')"
          :empty-text="$t('system.label.noData')"
          :item-aria-label="mobileItemAriaLabel"
          :item-key="mobileItemKey"
          @item-click="openOrderDetails"
        >
          <template #item="{ item, ariaLabel, onClick }">
            <CardListItem
              :aria-label="ariaLabel"
              @click="onClick()"
            >
              <BlockContainer :rows="mobileCardFields(item)" />
            </CardListItem>
          </template>
        </CardList>
      </div>
    </div>

    <!-- Modal for Order Details -->
    <Modal
      v-model="showOrderDetailsModal"
      :title="$t('orderDetails.title')"
      ref="orderDetailsModal"
    >
      <OrderDetails
        v-if="currentOrderSelected && currentOrderSelected.guid"
        :guid="currentOrderSelected.guid"
        :shopId="currentOrderSelected.shopId"
      />

      <template #footer v-if="modalConfirmVisible || modalCancelVisible">
        <Button
          v-if="modalConfirmVisible"
          :disabled="isLoading"
          icon-left="circle-check"
          class="confirm-order-button"
          @click.stop="onConfirmOrder(currentOrderSelected)"
        >
          {{ $t('orderDetails.button.confirm') }}
        </Button>
        <Button
          v-if="modalCancelVisible"
          :disabled="isLoading"
          icon-left="ban"
          class="cancel-order-button"
          @click.stop="openCancelDialog(currentOrderSelected)"
        >
          {{ $t('orderDetails.button.cancel') }}
        </Button>
      </template>
    </Modal>

    <ConfirmDialog
      v-model="showCancelDialog"
      :title="$t('orderHistory.cancelDialog.title')"
      :message="$t('orderHistory.cancelDialog.text')"
      :okLabel="$t('orderHistory.cancelDialog.okLabel')"
      :cancelLabel="$t('orderHistory.cancelDialog.cancelLabel')"
      :okDisabled="isLoading"
      @confirm="onCancelOrder(currentOrderSelected)"
      @cancel="showCancelDialog = false"
    />
  </div>
</template>

<script>

import Utils from "@/utils/utils";
import DateSearchTable from "@/components/main-components/DateSearchTable.vue";
import VueLoading from "vue-loading-overlay";
import orderMixin from "@/components/mixins/orderMixin";
import {OrderStatusId} from "@/constants/constants";
import Table from "@/components/data-display/Table.vue";
import OrderDetails from "@/components/sub-components/Order/OrderDetails.vue";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import Button from "@/components/actions/Button.vue";
import Modal from "@/components/feedback/Modal.vue";
import CardList from "@/components/data-display/CardList.vue";
import CardListItem from "@/components/data-display/CardListItem.vue";
import ConfirmDialog from "../../feedback/ConfirmDialog.vue";

export default {
  mixins: [orderMixin],
  data() {
    return {
      isLoading: false,
      /** @type {[{barcode:string,date:string,formattedDate:string,formattedTotal:string,guid:string,mealplanId:number,shopCode:string,shopDescription:string,shopId:number,status:string,statusCode:string,success:boolean,totalAmount:number,transComment:string}]} */
      recentOrders: [],
      /** @type {{barcode:string,date:string,formattedDate:string,formattedTotal:string,guid:string,mealplanId:number,shopCode:string,shopDescription:string,shopId:number,status:string,statusCode:string,success:boolean,totalAmount:number,transComment:string}} */
      currentOrderSelected: {},
      showOrderDetailsModal: false,
      showCancelDialog: false,
    }
  },
  computed: {
    Utils() {
      return Utils
    },
    fieldsWithActions() {
      return [
        { key: 'date', label: this.$t('orderHistory.tableColumn.date'), sortable: true, formatter: (value, key, item) => item.formattedDate },
        { key: 'shopDescription', label: this.$t('orderHistory.tableColumn.shop'), sortable: true, tdClass: 'text-left' },
        { key: 'status', label: this.$t('orderHistory.tableColumn.status'), sortable: true },
        { key: 'totalAmount', label: this.$t('orderHistory.tableColumn.amount'), sortable: true, class: 'text-right', formatter: (value, key, item) => item.formattedTotal ? item.formattedTotal : '' },
        { key: 'actions', label: this.$t('orderHistory.tableColumn.actions'), tdClass: 'text-right' }
      ];
    },
    tableActions() {
      return [
        {
          icon: ['fas', 'circle-info'],
          tooltip: this.$t('orderHistory.tooltip.details'),
          event: 'details',
          show: () => true,
          class: 'order-details-button'
        },
        {
          icon: ['fas', 'ban'],
          tooltip: this.$t('orderHistory.tooltip.cancel'),
          event: 'cancel-order',
          show: (item) => this.$config.ORDER_HISTORY.details.cancellation && (item.statusId === OrderStatusId.ORDERED || item.statusId === OrderStatusId.READY_TO_BE_PREPARED),
          class: 'cancel-order-button',
          handler: (item) => this.openCancelDialog(item)
        }
      ];
    },
    modalConfirmVisible() {
      const statusId = this.currentOrderSelected && this.currentOrderSelected.statusId;
      return !!(
        this.$config?.ORDER_HISTORY?.details?.confirmation &&
        (statusId === OrderStatusId.ORDERED || statusId === OrderStatusId.READY_TO_BE_PREPARED)
      );
    },
    modalCancelVisible() {
      const statusId = this.currentOrderSelected && this.currentOrderSelected.statusId;
      return !!(
        this.$config?.ORDER_HISTORY?.details?.cancellation &&
        (statusId === OrderStatusId.ORDERED || statusId === OrderStatusId.READY_TO_BE_PREPARED)
      );
    },
  },
  components: {
    ConfirmDialog,
    CardList,
    CardListItem,
    Modal,
    Button,
    BlockContainer,
    OrderDetails,
    Table,
    VueLoading,
    DateSearchTable,
  },
  methods: {
    onSearch() {
      // Only one dateSearchTable ref is present at a time (desktop or mobile)
      if (this.$refs.dateSearchTable) {
        this.$refs.dateSearchTable.search();
      }
      // Scroll to transaction overview after search
      this.$nextTick(() => {
        if (this.$refs.orderOverview && this.$refs.orderOverview.$el) {
          this.$refs.orderOverview.$el.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    mobileCardFields(item) {
      return [
        { key: 'shopDescription', label: this.$t('orderHistory.tableColumn.shop'), value: item.shopDescription },
        { key: 'date', label: this.$t('orderHistory.tableColumn.date'), value: item.formattedDate },
        { key: 'status', label: this.$t('orderHistory.tableColumn.status'), value: item.status },
        { key: 'total', label: this.$t('orderHistory.tableColumn.amount'), value: item.formattedTotal },
      ];
    },
    mobileItemAriaLabel(item) {
      return this.$t('orderHistory.tooltip.orderFrom', [item.formattedDate]);
    },
    mobileItemKey(item, index) {
      return item.guid || item.id || index;
    },
    async onConfirmOrder(item) {
      this.isLoading = true;
      const success = await this.confirmOrder(item.guid, item.tableId, item.shopId);
      if (success) {
        await this.retrieveOrders(this.Utils.getStartOfMonth(), this.Utils.getEndOfMonth());
      }
      this.isLoading = false;
      this.showOrderDetailsModal = false; // Close modal after confirm
    },
    openCancelDialog(item) {
      this.currentOrderSelected = item;
      this.showCancelDialog = true;
    },
    async onCancelOrder(item) {
      this.isLoading = true;
      const shopStore = { wondId: item.shopId };
      const success = await this.cancelOrder(shopStore, item.guid);
      if (success) {
        await this.retrieveOrders(this.Utils.getStartOfMonth(), this.Utils.getEndOfMonth());
      }
      this.isLoading = false;
      this.showOrderDetailsModal = false; // Close modal after cancel
      this.showCancelDialog = false;
    },
    openOrderDetails(item) {

      this.currentOrderSelected = item;
      this.showOrderDetailsModal = true;
    },
    showOrderDetails(item) {
      this.openOrderDetails(item);
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    rowIdentifier(item, type) {
      if (type !== 'row') {
        return {};
      }
      return {
        'title': this.$t('orderHistory.tooltip.orderFrom') + ' ' + item.formattedDate
      }
    },
  },
  async created() {
    const { dateFrom, dateTo } = this.$route.query || {};
    if (dateFrom && dateTo) {
      await this.retrieveOrders(dateFrom, dateTo);
    } else {
      await this.retrieveOrders(Utils.getStartOfMonth(), Utils.getEndOfMonth());
    }
  },
}
</script>

<style>
.cancel-order-button {
  color: #fff !important;
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
}

.order-history-desktop-stack,
.order-history-mobile-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
